import { BLOG_PAGE_TYPES, CustomPageType } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useCallback, useMemo } from 'react';
import { Toggle } from '../../../../../web/components/Toggle';
import { bs } from '../../../../../web/styles/styles';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { Column, Row } from '../../../../components/Layout';
import { styleSheet } from '../../../../components/blogs/styles';

interface FeatureToggleSectionProps {
	/** Current enabledFor array from the form */
	enabledFor?: CustomPageType[];
	/** Callback when enabledFor changes */
	onEnabledForChange: (enabledFor: CustomPageType[]) => void;
	/** Whether blog feature is available for this account */
	blogFeatureAvailable?: boolean;
	/** Whether survey feature is available for this account */
	surveyFeatureAvailable?: boolean;
	/** Whether meeting scheduler feature is available for this account */
	meetingFeatureAvailable?: boolean;
	disabled?: boolean;
}

/**
 * Converts enabledFor array to toggle states.
 * Empty array means all features are enabled.
 */
function getToggleStates(enabledFor: CustomPageType[] | undefined): {
	blogsEnabled: boolean;
	surveysEnabled: boolean;
	meetingsEnabled: boolean;
} {
	// Empty or undefined means all are enabled
	if (!enabledFor || enabledFor.length === 0) {
		return { blogsEnabled: true, surveysEnabled: true, meetingsEnabled: true };
	}

	// Check if any blog page types are present
	const blogsEnabled = BLOG_PAGE_TYPES.some(type => enabledFor.includes(type));
	const surveysEnabled = enabledFor.includes(CustomPageType.Survey);
	const meetingsEnabled = enabledFor.includes(CustomPageType.Meeting);

	return { blogsEnabled, surveysEnabled, meetingsEnabled };
}

/**
 * Converts toggle states to enabledFor array.
 * If all are enabled, returns empty array (applies to all).
 */
function getEnabledForArray(
	blogsEnabled: boolean,
	surveysEnabled: boolean,
	meetingsEnabled: boolean
): CustomPageType[] {
	// If all are enabled, return empty array (applies to all)
	if (blogsEnabled && surveysEnabled && meetingsEnabled) {
		return [];
	}

	const result: CustomPageType[] = [];

	if (blogsEnabled) {
		result.push(...BLOG_PAGE_TYPES);
	}
	if (surveysEnabled) {
		result.push(CustomPageType.Survey);
	}
	if (meetingsEnabled) {
		result.push(CustomPageType.Meeting);
	}

	return result;
}

/**
 * Feature toggle section for controlling which features use the custom header/footer styles.
 * Only shows toggles for features that are available on the account.
 *
 * Mapping to enabledFor array:
 * - All toggles ON -> enabledFor = [] (empty, applies to all)
 * - Specific toggles ON -> enabledFor contains corresponding page types
 */
export const FeatureToggleSection: FC<FeatureToggleSectionProps> = ({
	enabledFor,
	onEnabledForChange,
	blogFeatureAvailable = false,
	surveyFeatureAvailable = true,
	meetingFeatureAvailable = true,
	disabled = false,
}) => {
	const { blogsEnabled, surveysEnabled, meetingsEnabled } = useMemo(() => getToggleStates(enabledFor), [enabledFor]);

	const handleToggleBlogs = useCallback(
		(enabled: boolean) => {
			const newEnabledFor = getEnabledForArray(enabled, surveysEnabled, meetingsEnabled);
			onEnabledForChange(newEnabledFor);
		},
		[surveysEnabled, meetingsEnabled, onEnabledForChange]
	);

	const handleToggleSurveys = useCallback(
		(enabled: boolean) => {
			const newEnabledFor = getEnabledForArray(blogsEnabled, enabled, meetingsEnabled);
			onEnabledForChange(newEnabledFor);
		},
		[blogsEnabled, meetingsEnabled, onEnabledForChange]
	);

	const handleToggleMeetings = useCallback(
		(enabled: boolean) => {
			const newEnabledFor = getEnabledForArray(blogsEnabled, surveysEnabled, enabled);
			onEnabledForChange(newEnabledFor);
		},
		[blogsEnabled, surveysEnabled, onEnabledForChange]
	);

	const hasAnyFeature = blogFeatureAvailable || surveyFeatureAvailable || meetingFeatureAvailable;

	if (!hasAnyFeature) {
		return null;
	}

	return (
		<Column className={css(bs.mt4, bs.pt4, bs.borderT, bs.borderGray200)}>
			<Row className={css(bs.itemsCenter, bs.gap2, bs.mb4)}>
				<h3 className={css(styleSheet.subtitleText)}>Apply to Features</h3>
				<InfoPopover message='Choose which features should use this custom styling. When disabled, the feature will use default system styling.' />
			</Row>
			<Row className={css(bs.gap4, bs.flexWrap)}>
				{blogFeatureAvailable && (
					<Row className={css(bs.itemsCenter, bs.gap2)}>
						<Toggle
							id='feature-toggle-blogs'
							isOn={blogsEnabled}
							onToggleCheckChanged={handleToggleBlogs}
							disabled={disabled}
						/>
						<label htmlFor='feature-toggle-blogs' className={css(bs.textSm, bs.textGray600)}>
							Blogs
						</label>
					</Row>
				)}
				{surveyFeatureAvailable && (
					<Row className={css(bs.itemsCenter, bs.gap2)}>
						<Toggle
							id='feature-toggle-surveys'
							isOn={surveysEnabled}
							onToggleCheckChanged={handleToggleSurveys}
							disabled={disabled}
						/>
						<label htmlFor='feature-toggle-surveys' className={css(bs.textSm, bs.textGray600)}>
							Surveys
						</label>
					</Row>
				)}
				{meetingFeatureAvailable && (
					<Row className={css(bs.itemsCenter, bs.gap2)}>
						<Toggle
							id='feature-toggle-meetings'
							isOn={meetingsEnabled}
							onToggleCheckChanged={handleToggleMeetings}
							disabled={disabled}
						/>
						<label htmlFor='feature-toggle-meetings' className={css(bs.textSm, bs.textGray600)}>
							Meetings
						</label>
					</Row>
				)}
			</Row>
		</Column>
	);
};
